import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { storagePeriodPrefix } from '../../../lib/storage-path';
import { useVertical } from '../../../context/VerticalContext';
// Note: no `supplier` key exists on the vertical Jargon type, so supplier copy
// stays literal here (matching the surrounding Returns/Credits UI). Kept
// useVertical() for activeOrgId, which is the source of truth for org scoping.
import { X, UploadCloud, File, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { errorMessage } from '../../../lib/errors';

interface CreditNoteUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Refresh parent (e.g. pending credits) once a note is queued for OCR.
    onSuccess?: () => void;
    title?: string;
    subtitle?: string;
}

interface UploadStatus {
    id: string;
    file: File;
    status: 'pending' | 'uploading' | 'queued' | 'error';
    error?: string;
}

// Mirror ReceiptUploadModal's accept list + limits. HEIC/WEBP are added here
// because phone-camera captures of paper credit notes commonly arrive as HEIC.
const ACCEPTED_MIME = [
    'image/jpeg',
    'image/png',
    'image/jpg',
    'image/webp',
    'image/heic',
    'image/heif',
    'application/pdf',
];
const ACCEPTED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB, same as receipts

function isAcceptedFile(file: File): boolean {
    if (file.type && ACCEPTED_MIME.includes(file.type)) return true;
    // HEIC often reports an empty type in browsers — fall back to extension.
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return ACCEPTED_EXT.includes(ext);
}

export function CreditNoteUploadModal({
    isOpen,
    onClose,
    onSuccess,
    title,
    subtitle,
}: CreditNoteUploadModalProps) {
    const { activeOrgId } = useVertical();
    const [userId, setUserId] = useState<string | null>(null);
    const [uploads, setUploads] = useState<UploadStatus[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => {
            setUserId(data.session?.user?.id || null);
        });
    }, []);

    const processUpload = async (upload: UploadStatus) => {
        if (!userId || !activeOrgId) {
            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'error',
                error: 'Not signed in or no active organisation.',
            } : u));
            return false;
        }

        try {
            setUploads(prev => prev.map(u => u.id === upload.id ? { ...u, status: 'uploading' } : u));

            // Shared bucket layout: {org}/{YYYY}/Q{quarter}/{MM}/{uuid}.{ext}
            // Period-by-upload-date is a filing convenience only. The authoritative
            // period for the accountant-pack export is credit_notes.doc_date, which
            // the OCR pipeline fills — not this path.
            const ext = (upload.file.name.split('.').pop() || 'bin').toLowerCase();
            const filePath = `${storagePeriodPrefix(activeOrgId)}/${crypto.randomUUID()}.${ext}`;

            const { error: uploadError } = await supabase.storage
                .from('credit-notes')
                .upload(filePath, upload.file, {
                    cacheControl: '3600',
                    upsert: false,
                });

            if (uploadError) throw uploadError;

            // Insert queue row. doc_date left null — OCR fills credit_notes.doc_date.
            // doc_type defaults to 'credit_note' server-side; status defaults 'pending'.
            const { error: queueError } = await supabase
                .from('credit_note_queue')
                .insert({
                    org_id: activeOrgId,
                    file_path: filePath,
                    file_name: upload.file.name,
                    file_type: upload.file.type || 'application/octet-stream',
                    metadata: { uploaded_by: userId },
                    status: 'pending',
                });

            if (queueError) throw queueError;

            setUploads(prev => prev.map(u => u.id === upload.id ? { ...u, status: 'queued' } : u));
            return true;
        } catch (err) {
            // Do not swallow — surface the real reason.
            console.error('CreditNoteUploadModal: upload/queue failed', err);
            setUploads(prev => prev.map(u => u.id === upload.id ? {
                ...u,
                status: 'error',
                error: errorMessage(err) || 'Upload failed',
            } : u));
            return false;
        }
    };

    const handleFilesSelected = async (files: FileList | null) => {
        if (!files || files.length === 0 || isBusy) return;

        const fileArray = Array.from(files);

        // Client-side validation before anything hits the network.
        const rejected: string[] = [];
        const accepted: File[] = [];
        for (const file of fileArray) {
            if (!isAcceptedFile(file)) {
                rejected.push(`${file.name} (unsupported type)`);
            } else if (file.size > MAX_FILE_BYTES) {
                rejected.push(`${file.name} (over 10MB)`);
            } else {
                accepted.push(file);
            }
        }
        if (rejected.length > 0) {
            toast.error(`Skipped: ${rejected.join(', ')}`);
        }
        if (accepted.length === 0) return;

        const newUploads: UploadStatus[] = accepted.map(file => ({
            id: crypto.randomUUID(),
            file,
            status: 'pending' as const,
        }));
        setUploads(prev => [...prev, ...newUploads]);

        setIsBusy(true);
        const results = await Promise.all(newUploads.map(u => processUpload(u)));
        const successCount = results.filter(Boolean).length;

        // Fire-and-forget the isolated credit OCR trigger. Never hard-fail the
        // UI on a trigger error — the row is queued and can be retried server-side.
        if (successCount > 0) {
            try {
                const { error: triggerError } = await supabase.functions.invoke('ocr-credit-queue-trigger');
                if (triggerError) {
                    console.error('CreditNoteUploadModal: ocr-credit-queue-trigger failed', triggerError);
                }
            } catch (e) {
                console.error('CreditNoteUploadModal: ocr-credit-queue-trigger threw', e);
            }

            toast.success(
                successCount === 1
                    ? 'Credit note uploaded — extracting & matching…'
                    : `${successCount} credit notes uploaded — extracting & matching…`,
                { duration: 4000 }
            );
            onSuccess?.();
        }
        setIsBusy(false);
    };

    const handleClose = () => {
        if (isBusy) return;
        setUploads([]);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-panel border border-line rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-line bg-raised">
                    <div>
                        <h2 className="text-xl font-bold text-fg">{title || 'Upload credit note'}</h2>
                        <p className="text-sm text-fg-3 mt-1">
                            {subtitle || "We'll extract the credit and match it to your supplier returns automatically."}
                        </p>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isBusy}
                        className="p-2 hover:bg-raised rounded-full transition-colors text-fg-3 hover:text-fg shrink-0 disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto custom-scrollbar">
                    {/* Dropzone */}
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
              ${isDragging ? 'border-accent bg-accent/10' : 'border-line-strong hover:border-line-glow bg-card/50'}
              ${isBusy ? 'opacity-60 pointer-events-none' : ''}
            `}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragging(false);
                            handleFilesSelected(e.dataTransfer.files);
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <UploadCloud className="w-10 h-10 text-fg-3 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-fg mb-2">Click or drag a credit note here</h3>
                        <p className="text-sm text-fg-3 mb-4 max-w-md mx-auto">
                            Supports PDF, JPG, PNG, WEBP, HEIC (Max 10MB per file). You can select multiple files.
                        </p>
                        <button
                            type="button"
                            className="bg-raised hover:bg-pressed text-fg px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
                        >
                            Browse Files
                        </button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            multiple
                            accept="image/jpeg,image/png,image/jpg,image/webp,image/heic,image/heif,application/pdf"
                            onChange={(e) => handleFilesSelected(e.target.files)}
                        />
                    </div>

                    {/* Upload List */}
                    {uploads.length > 0 && (
                        <div className="mt-8 space-y-3">
                            <h4 className="text-sm font-medium text-fg-3 uppercase tracking-wider mb-4">Uploads</h4>
                            {uploads.map((upload) => (
                                <div key={upload.id} className="bg-card border border-line rounded-lg p-4 flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-4 flex-1 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-raised flex items-center justify-center shrink-0">
                                            {upload.file.type.includes('pdf')
                                                ? <File className="w-5 h-5 text-info" />
                                                : <ImageIcon className="w-5 h-5 text-info" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-fg truncate">{upload.file.name}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-fg-4">{(upload.file.size / 1024 / 1024).toFixed(2)} MB</span>
                                                <span className="text-fg-4">&bull;</span>
                                                <span className="text-xs font-medium" style={{ color: statusColor(upload.status) }}>
                                                    {statusText(upload.status)}
                                                </span>
                                            </div>
                                            {upload.error && (
                                                <p className="text-xs text-danger mt-1 line-clamp-2">{upload.error}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="shrink-0 flex items-center">
                                        {(upload.status === 'uploading' || upload.status === 'pending') && (
                                            <Loader2 className="w-5 h-5 text-accent animate-spin" />
                                        )}
                                        {upload.status === 'queued' && (
                                            <CheckCircle2 className="w-5 h-5 text-success" />
                                        )}
                                        {upload.status === 'error' && (
                                            <AlertCircle className="w-5 h-5 text-danger" />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-line bg-raised flex justify-end">
                    <button
                        onClick={handleClose}
                        disabled={isBusy}
                        className="fd-btn-primary disabled:opacity-50 px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                    >
                        {isBusy ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading…
                            </>
                        ) : (
                            <>Done</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function statusColor(status: UploadStatus['status']): string {
    switch (status) {
        case 'queued': return 'var(--fd-success)';
        case 'error': return 'var(--fd-danger)';
        case 'uploading':
        case 'pending':
        default: return 'var(--fds-accent)';
    }
}

function statusText(status: UploadStatus['status']): string {
    switch (status) {
        case 'pending': return 'Preparing…';
        case 'uploading': return 'Uploading…';
        case 'queued': return 'Queued for matching';
        case 'error': return 'Upload failed';
        default: return status;
    }
}
